// Iter122av · Dashboard Credius: sinossi pratiche in corso + solleciti (spostati da Pannello account)
import { useEffect, useState, useMemo } from "react";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { getBandCredius, getPillCredius } from "./SharedCard";
import { toast } from "sonner";
import { RefreshCw, Scale, Send, Bell, ClipboardList } from "lucide-react";

export function CrediusDashboard({ pratiche = [] }) {
  const [crediusGiorni, setCrediusGiorni] = useState(30);
  const [busy, setBusy] = useState(false);
  // Iter122aw · ricerca rapida in dashboard
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/settings")
      .then((r) => setCrediusGiorni(r.data.credius_sollecito_giorni || 30))
      .catch(() => {});
  }, []);

  const stats = useMemo(() => {
    const p = pratiche || [];
    const aperte = p.filter((x) => x.stato !== "Chiusa" && x.stato !== "Archiviata");
    const perStato = {};
    for (const x of aperte) perStato[x.stato] = (perStato[x.stato] || 0) + 1;
    const perTipo = {};
    for (const x of aperte) perTipo[x.tipo] = (perTipo[x.tipo] || 0) + 1;
    // Iter122aw · applica filtro testuale
    const needle = (q || "").trim().toLowerCase();
    const filtrate = needle
      ? aperte.filter((x) => [x.numero, x.oggetto, x.tipo, x.stato, x.avvocato_nome].some((v) => (v || "").toLowerCase().includes(needle)))
      : aperte;
    const ultime = [...filtrate].sort((a, b) => (b.updated_at || "").localeCompare(a.updated_at || "")).slice(0, 20);
    return { totale: aperte.length, filtrate: filtrate.length, perStato, perTipo, ultime };
  }, [pratiche, q]);

  const salvaGiorni = async () => {
    const n = parseInt(crediusGiorni, 10);
    if (!(n >= 1 && n <= 365)) { toast.error("Indica un valore tra 1 e 365 giorni"); return; }
    setBusy(true);
    try {
      await api.put("/settings", { credius_sollecito_giorni: n });
      toast.success("Sollecito Credius salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Salvataggio non riuscito"); }
    setBusy(false);
  };

  const eseguiSolleciti = async () => {
    if (!window.confirm(`Inviare subito i solleciti alle pratiche ferme da più di ${crediusGiorni} giorni?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/credius/run-solleciti");
      toast.success(`Solleciti inviati: ${data.inviati} (limite ${data.giorni_limite} gg)`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore invio solleciti"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="credius-dashboard-page">
      {/* Sinossi */}
      <Card className="p-4 border-2" data-testid="credius-dashboard-riepilogo">
        <div className="flex items-center gap-2 mb-3">
          <Scale className="w-5 h-5 text-indigo-800" />
          <h2 className="font-display text-xl font-bold text-slate-900">Sintesi pratiche in corso</h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="p-3 rounded-lg bg-indigo-50 border border-indigo-200">
            <div className="text-[11px] font-bold uppercase text-indigo-700">Totale aperte</div>
            <div className="text-2xl font-extrabold text-indigo-900" data-testid="credius-dashboard-count">{stats.totale}</div>
          </div>
          {Object.entries(stats.perStato).slice(0, 3).map(([s, n]) => (
            <div key={s} className="p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div className="text-[11px] font-bold uppercase text-slate-600">{s}</div>
              <div className="text-2xl font-extrabold text-slate-800">{n}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
            <ClipboardList className="w-3.5 h-3.5" /> Ultimi aggiornamenti {q && <span className="text-slate-400 font-normal">· {stats.filtrate} risultati</span>}
          </div>
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="credius-dashboard-search"
            placeholder="Cerca numero, oggetto, avvocato…"
            className="h-9 w-full sm:w-64 text-sm" />
        </div>
        {stats.ultime.length === 0 ? (
          <div className="text-sm text-slate-400 italic">{q ? `Nessuna pratica corrisponde a «${q}».` : "Nessuna pratica aperta."}</div>
        ) : (
          <div className="space-y-1.5">
            {stats.ultime.map((p) => {
              const bordo = getBandCredius(p);
              return (
              <a key={p.id} href={`/credius/${p.id}`}
                data-testid={`credius-dashboard-row-${p.id}`}
                className={`flex flex-wrap items-center gap-2 py-2 pr-2 pl-3 text-sm bg-white border border-slate-200 rounded-md hover:bg-slate-50 hover:border-l-[12px] transition-[border-left-width,background-color] duration-150 ${bordo}`}>
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800">{p.numero}</span>
                <span className="font-semibold text-slate-800 truncate max-w-[280px]">{p.oggetto || "—"}</span>
                <span className="text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{p.tipo}</span>
                <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded border ${getPillCredius(p.stato)}`}>{p.stato}</span>
                <span className="text-[10px] text-slate-400 ml-auto">{p.avvocato_nome}</span>
              </a>
              );
            })}
          </div>
        )}
      </Card>

      {/* Solleciti */}
      <Card className="p-4 border-2" data-testid="credius-dashboard-solleciti">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-5 h-5 text-amber-700" />
          <h2 className="font-display text-xl font-bold text-slate-900">Solleciti di aggiornamento</h2>
        </div>
        <p className="text-sm text-slate-600 mb-3">
          Ogni notte alle 09:30 il sistema invia agli avvocati un promemoria sulle pratiche ferme da più di <b>N</b> giorni.
          Le pratiche già &quot;Concluse&quot; o &quot;Revocate&quot; sono escluse.
        </p>
        <div className="flex flex-wrap items-end gap-3 mt-3">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">Giorni senza aggiornamento</label>
            <Input type="number" min="1" max="365" value={crediusGiorni}
              onChange={(e) => setCrediusGiorni(e.target.value)}
              data-testid="credius-solleciti-giorni" className="h-11 w-32 mt-1" />
          </div>
          <Button onClick={salvaGiorni} disabled={busy}
            data-testid="credius-solleciti-salva" className="h-11 bg-indigo-800 hover:bg-indigo-900">
            Salva soglia
          </Button>
          <Button onClick={eseguiSolleciti} disabled={busy} variant="outline"
            data-testid="credius-solleciti-esegui" className="h-11">
            <Send className="w-4 h-4 mr-2" /> Esegui adesso
          </Button>
          <Button onClick={() => window.location.reload()} variant="ghost"
            data-testid="credius-dashboard-refresh" className="h-11 ml-auto">
            <RefreshCw className="w-4 h-4 mr-1" /> Ricarica
          </Button>
        </div>
      </Card>
    </div>
  );
}
